import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CleaningDetailService from '../../services/cleaningDetail.service';
import RecurrenceService from '../../services/recurrence.service';
import ClientDataService from '../../services/client.service';
import EmployeeService from '../../services/employee.service';
import { useTranslation } from '../../hooks/useTranslation';

const defaultRecurrenceForm = {
  schedule: 'weekly',
  start_date: new Date().toISOString().slice(0, 10),
  end_date: '',
  start_time: '09:00',
  every_days: 1,
  every_weeks: 2,
  day_of_week: 1,
  day_of_month: 1,
};

/** When false, income follows horas × hourly_rate; set true after user edits income. */
const incomeDiffersFromProduct = (horasStr, rateStr, incomeStr) => {
  const h = parseFloat(horasStr);
  const r = parseFloat(rateStr);
  const inc = parseFloat(incomeStr);
  if (!Number.isFinite(h) || !Number.isFinite(r) || !Number.isFinite(inc)) return true;
  const product = h * r;
  return Math.abs(inc - product) > 0.015;
};

const CleaningDetailForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [cleanerEmployeeIds, setCleanerEmployeeIds] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    recurrence_id: '',
    notes: '',
    horas: '',
    income: '0',
    wages: '0',
    hourly_rate: '',
  });
  const [recurrences, setRecurrences] = useState([]);
  const [recDialogOpen, setRecDialogOpen] = useState(false);
  const [recForm, setRecForm] = useState(defaultRecurrenceForm);
  const [savingRec, setSavingRec] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [incomeManualOverride, setIncomeManualOverride] = useState(false);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [savingClient, setSavingClient] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', phone: '', email: '' });
  const [copyingDefaults, setCopyingDefaults] = useState(false);
  const [cleanerDialogOpen, setCleanerDialogOpen] = useState(false);
  const [cleanerPickerQuery, setCleanerPickerQuery] = useState('');

  const loadClients = useCallback(async () => {
    try {
      const cRes = await ClientDataService.getClients();
      const raw = cRes.data?.data ?? cRes.data ?? [];
      setClients(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadRecurrences = useCallback(async () => {
    try {
      const res = await RecurrenceService.getAll();
      const raw = res.data?.data ?? res.data;
      setRecurrences(Array.isArray(raw) ? raw : []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadClients();
    loadRecurrences();
    (async () => {
      try {
        const eRes = await EmployeeService.getAll();
        const rawE = eRes.data?.data ?? [];
        setEmployees(Array.isArray(rawE) ? rawE : []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [loadClients, loadRecurrences]);

  useEffect(() => {
    const qClient = searchParams.get('clientId');
    if (!isEdit && qClient) {
      setForm((f) => ({ ...f, client_id: String(qClient) }));
    }
  }, [isEdit, searchParams]);

  useEffect(() => {
    if (!isEdit) {
      setCleanerEmployeeIds([]);
    }
  }, [isEdit]);

  useEffect(() => {
    const openNewClient = searchParams.get('newClient');
    if (!isEdit && (openNewClient === '1' || openNewClient === 'true')) {
      setClientForm({ name: '', phone: '', email: '' });
      setClientDialogOpen(true);
    }
  }, [isEdit, searchParams]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      setLoading(true);
      try {
        const res = await CleaningDetailService.get(id);
        const d = res.data?.data;
        const horasStr = d.horas != null ? String(d.horas) : '';
        const hourlyStr = d.hourly_rate != null ? String(d.hourly_rate) : '';
        const incomeStr = d.income != null ? String(d.income) : '0';
        setForm({
          client_id: String(d.client_id),
          recurrence_id: d.recurrence_id != null && d.recurrence_id !== '' ? String(d.recurrence_id) : '',
          notes: d.notes || '',
          horas: horasStr,
          income: incomeStr,
          wages: d.wages != null ? String(d.wages) : '0',
          hourly_rate: hourlyStr,
        });
        const cleanerIds = (d.cleaners || [])
          .map((c) => c?.employee_id ?? c?.employee?.id)
          .filter((x) => x != null)
          .map((x) => String(x));
        setCleanerEmployeeIds(Array.from(new Set(cleanerIds)));
        setIncomeManualOverride(incomeDiffersFromProduct(horasStr, hourlyStr, incomeStr));
      } catch (e) {
        setSnackbar({ open: true, message: t('cleaningDetails.loadError'), severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, t]);

  const selectedCleanerEmployees = useMemo(
    () => employees.filter((e) => cleanerEmployeeIds.includes(String(e.id))),
    [employees, cleanerEmployeeIds]
  );

  const cleanersPickList = useMemo(() => {
    const all = Array.isArray(employees) ? employees : [];
    const active = all.filter((e) => !e?.status || String(e.status).toLowerCase() === 'active');
    const q = cleanerPickerQuery.trim().toLowerCase();
    return active
      .filter((e) => !cleanerEmployeeIds.includes(String(e.id)))
      .filter((e) => !q || (e.name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [employees, cleanerEmployeeIds, cleanerPickerQuery]);

  const removeCleaner = (employeeIdStr) => {
    setCleanerEmployeeIds((prev) => prev.filter((x) => x !== employeeIdStr));
  };

  const addCleanerFromPicker = (emp) => {
    if (!emp?.id) return;
    const idStr = String(emp.id);
    setCleanerEmployeeIds((prev) => (prev.includes(idStr) ? prev : [...prev, idStr]));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'income') {
      setIncomeManualOverride(true);
      setForm((prev) => ({ ...prev, income: value }));
      return;
    }
    if (name === 'horas' || name === 'hourly_rate') {
      setForm((prev) => {
        const next = { ...prev, [name]: value };
        if (!incomeManualOverride) {
          const h = parseFloat(name === 'horas' ? value : prev.horas);
          const r = parseFloat(name === 'hourly_rate' ? value : prev.hourly_rate);
          if (Number.isFinite(h) && Number.isFinite(r)) {
            next.income = String(Math.round(h * r * 100) / 100);
          } else {
            next.income = '0';
          }
        }
        return next;
      });
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleClientFormChange = (e) => {
    const { name, value } = e.target;
    setClientForm((prev) => ({ ...prev, [name]: value }));
  };

  const openNewClientDialog = () => {
    setClientForm({ name: '', phone: '', email: '' });
    setClientDialogOpen(true);
  };

  const saveClient = async () => {
    if (!clientForm.name.trim()) {
      setSnackbar({ open: true, message: t('cleaningDetails.clientNameRequired'), severity: 'error' });
      return;
    }
    setSavingClient(true);
    try {
      const res = await ClientDataService.createClient({
        name: clientForm.name.trim(),
        phone: clientForm.phone?.trim() || null,
        email: clientForm.email?.trim() || null,
        field_values: [],
      });
      const created = res.data?.data ?? res.data;
      const newId =
        created && typeof created === 'object'
          ? created.id ?? created.data?.id
          : null;
      await loadClients();
      if (newId != null && newId !== '') {
        setForm((f) => ({ ...f, client_id: String(newId) }));
      }
      setClientDialogOpen(false);
      setSnackbar({ open: true, message: t('cleaningDetails.clientCreated'), severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningDetails.clientCreateError'),
        severity: 'error',
      });
    } finally {
      setSavingClient(false);
    }
  };

  const copyDefaultsFromScheduled = async () => {
    if (!form.client_id) return;
    setCopyingDefaults(true);
    try {
      const res = await CleaningDetailService.getAll({ client_id: form.client_id });
      const rows = res.data?.data ?? [];
      const list = Array.isArray(rows) ? rows : [];
      const scheduled = list.filter((x) => x).sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
      const pick = scheduled[0] || null;
      if (!pick) {
        setSnackbar({ open: true, message: t('cleaningDetails.noScheduledDefaults'), severity: 'info' });
        return;
      }
      const horasStr = pick.horas != null ? String(pick.horas) : '';
      const hourlyStr = pick.hourly_rate != null ? String(pick.hourly_rate) : '';
      const wagesStr = pick.wages != null ? String(pick.wages) : '0';
      const incomeStr =
        pick.income != null
          ? String(pick.income)
          : !incomeManualOverride && horasStr !== '' && hourlyStr !== ''
            ? String(Math.round(parseFloat(horasStr) * parseFloat(hourlyStr) * 100) / 100)
            : form.income;
      setIncomeManualOverride(incomeDiffersFromProduct(horasStr, hourlyStr, incomeStr));
      setForm((f) => ({
        ...f,
        recurrence_id:
          pick.recurrence_id != null && pick.recurrence_id !== '' ? String(pick.recurrence_id) : f.recurrence_id,
        horas: horasStr,
        hourly_rate: hourlyStr,
        wages: wagesStr,
        income: incomeStr,
      }));
      setSnackbar({ open: true, message: t('cleaningDetails.copiedDefaults'), severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningDetails.copyDefaultsError'),
        severity: 'error',
      });
    } finally {
      setCopyingDefaults(false);
    }
  };

  const goToCleaningSchedule = () => {
    if (form.client_id) {
      navigate(`/cleaning-schedule?client_id=${encodeURIComponent(form.client_id)}`);
      return;
    }
    navigate('/cleaning-schedule');
  };

  const handleRecChange = (e) => {
    const { name, value } = e.target;
    setRecForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveRecurrence = async () => {
    setSavingRec(true);
    try {
      const payload = {
        schedule: recForm.schedule,
        start_date: recForm.start_date,
        start_time: recForm.start_time.length === 5 ? recForm.start_time : recForm.start_time.slice(0, 5),
        every_days: recForm.schedule === 'daily' ? parseInt(recForm.every_days, 10) || 1 : 1,
      };
      if (recForm.end_date) payload.end_date = recForm.end_date;
      if (recForm.schedule === 'weekly') {
        payload.day_of_week = parseInt(recForm.day_of_week, 10);
      }
      if (recForm.schedule === 'every_n_weeks') {
        payload.day_of_week = parseInt(recForm.day_of_week, 10);
        payload.every_weeks = parseInt(recForm.every_weeks, 10) || 2;
      }
      if (recForm.schedule === 'monthly') {
        payload.day_of_month = parseInt(recForm.day_of_month, 10);
      }
      const res = await RecurrenceService.create(payload);
      const created = res.data?.data;
      const newId =
        created && typeof created === 'object'
          ? created.id ?? created.data?.id
          : null;
      await loadRecurrences();
      if (newId != null && newId !== '') {
        setForm((f) => ({ ...f, recurrence_id: String(newId) }));
      }
      setRecDialogOpen(false);
      setRecForm(defaultRecurrenceForm);
      setSnackbar({ open: true, message: t('cleaningDetails.recurrenceCreated'), severity: 'success' });
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningDetails.recurrenceError'),
        severity: 'error',
      });
    } finally {
      setSavingRec(false);
    }
  };

  const submit = async () => {
    if (!form.client_id) {
      setSnackbar({ open: true, message: t('cleaningDetails.fillRequired'), severity: 'error' });
      return;
    }
    if (form.horas === '' || form.hourly_rate === '') {
      setSnackbar({ open: true, message: t('cleaningDetails.fillRequired'), severity: 'error' });
      return;
    }
    const payload = {
      client_id: parseInt(form.client_id, 10),
      recurrence_id: form.recurrence_id ? parseInt(form.recurrence_id, 10) : null,
      notes: form.notes || null,
      horas: parseFloat(form.horas),
      income: parseFloat(form.income) || 0,
      wages: parseFloat(form.wages) || 0,
      hourly_rate: parseFloat(form.hourly_rate),
      cleaner_ids: cleanerEmployeeIds.map((x) => parseInt(x, 10)).filter((n) => Number.isFinite(n)),
    };
    try {
      setLoading(true);
      if (isEdit) {
        await CleaningDetailService.update(id, payload);
        setSnackbar({ open: true, message: t('cleaningDetails.updatedSuccess'), severity: 'success' });
      } else {
        await CleaningDetailService.create(payload);
        setSnackbar({ open: true, message: t('cleaningDetails.createdSuccess'), severity: 'success' });
      }
      navigate(
        form.client_id ? `/cleaning-details?clientId=${encodeURIComponent(form.client_id)}` : '/cleaning-details'
      );
    } catch (e) {
      setSnackbar({
        open: true,
        message: e.response?.data?.message || t('cleaningDetails.saveError'),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit && !form.client_id) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('cleaningDetails.edit') : t('cleaningDetails.new')}
            </Typography>
          </Box>
        }
        action={
          <Button variant="contained" startIcon={<SaveIcon />} onClick={submit} disabled={loading}>
            {t('common.save')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
              <FormControl fullWidth required sx={{ flex: 1 }}>
                <InputLabel id="cd-client" shrink={Boolean(form.client_id)}>
                  {t('cleaningDetails.client')}
                </InputLabel>
                <Select
                  labelId="cd-client"
                  label={t('cleaningDetails.client')}
                  name="client_id"
                  value={form.client_id}
                  onChange={handleChange}
                  notched={Boolean(form.client_id)}
                >
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={openNewClientDialog}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' }, mt: { sm: 0.5 } }}
              >
                {t('cleaningDetails.addClient')}
              </Button>
            </Box>
          </Grid>

          {!isEdit && (
            <Grid item xs={12}>
              <Button
                variant="text"
                disabled={!form.client_id || copyingDefaults}
                onClick={copyDefaultsFromScheduled}
              >
                {t('cleaningDetails.copyDefaultsFromScheduled')}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Alert
              severity="info"
              sx={{ borderRadius: 2, alignItems: 'center' }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  startIcon={<CalendarMonthIcon />}
                  onClick={goToCleaningSchedule}
                  sx={{ flexShrink: 0, borderColor: 'currentColor' }}
                >
                  {t('cleaningDetails.openSchedule')}
                </Button>
              }
            >
              {t('cleaningDetails.scheduleHint')}
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                alignItems: { sm: 'flex-start' },
              }}
            >
              <FormControl sx={{ flex: 1, minWidth: { xs: 0, sm: 300 }, width: { xs: '100%', sm: 'auto' } }}>
                <InputLabel id="cd-rec" shrink>
                  {t('cleaningDetails.recurrenceOptional')}
                </InputLabel>
                <Select
                  labelId="cd-rec"
                  label={t('cleaningDetails.recurrenceOptional')}
                  name="recurrence_id"
                  value={form.recurrence_id === undefined || form.recurrence_id === null ? '' : form.recurrence_id}
                  onChange={handleChange}
                  displayEmpty
                  notched
                  renderValue={(selected) => {
                    if (selected === '' || selected == null) {
                      return (
                        <Typography component="span" variant="body1" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {t('cleaningDetails.onDemandNoSchedule')}
                        </Typography>
                      );
                    }
                    const r = recurrences.find((x) => String(x.id) === String(selected));
                    const text = r?.label || `#${selected}`;
                    return (
                      <Typography component="span" variant="body1" noWrap sx={{ display: 'block' }}>
                        {text}
                      </Typography>
                    );
                  }}
                >
                  <MenuItem value="">
                    {t('cleaningDetails.onDemandNoSchedule')}
                  </MenuItem>
                  {recurrences.map((r) => (
                    <MenuItem key={r.id} value={String(r.id)}>
                      {r.label || `#${r.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setRecForm({
                    ...defaultRecurrenceForm,
                    start_date: new Date().toISOString().slice(0, 10),
                  });
                  setRecDialogOpen(true);
                }}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' }, mt: { sm: 0.5 } }}
              >
                {t('cleaningDetails.newSchedule')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('cleaningDetails.recurrenceFieldHelp')}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {t('cleaningDetails.cleaners')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5 }}>
              {selectedCleanerEmployees.map((e) => (
                <Chip key={e.id} label={e.name || `#${e.id}`} onDelete={() => removeCleaner(String(e.id))} />
              ))}
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  setCleanerPickerQuery('');
                  setCleanerDialogOpen(true);
                }}
              >
                {t('cleaningDetails.addCleaners')}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('cleaningDetails.cleanersHelper')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              name="notes"
              label={t('cleaningDetails.notes')}
              value={form.notes}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              required
              name="horas"
              label={t('cleaningDetails.horas')}
              type="number"
              inputProps={{ min: 0, step: '0.25' }}
              value={form.horas}
              onChange={handleChange}
              helperText={t('cleaningDetails.totalHoursHelper')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              required
              name="hourly_rate"
              label={t('cleaningDetails.hourlyRate')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.hourly_rate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              name="income"
              label={t('cleaningDetails.income')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.income}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              name="wages"
              label={t('cleaningDetails.wages')}
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
              value={form.wages}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </CardContent>

      <Dialog open={recDialogOpen} onClose={() => !savingRec && setRecDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cleaningDetails.newSchedule')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('cleaningDetails.scheduleType')}</InputLabel>
                <Select
                  name="schedule"
                  label={t('cleaningDetails.scheduleType')}
                  value={recForm.schedule}
                  onChange={handleRecChange}
                >
                  <MenuItem value="daily">{t('cleaningDetails.daily')}</MenuItem>
                  <MenuItem value="weekly">{t('cleaningDetails.weekly')}</MenuItem>
                  <MenuItem value="every_n_weeks">{t('cleaningDetails.everyNWeeks')}</MenuItem>
                  <MenuItem value="monthly">{t('cleaningDetails.monthly')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {recForm.schedule === 'daily' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="every_days"
                  label={t('cleaningDetails.everyDays')}
                  type="number"
                  inputProps={{ min: 1 }}
                  value={recForm.every_days}
                  onChange={handleRecChange}
                />
              </Grid>
            )}
            {recForm.schedule === 'weekly' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="day_of_week"
                  label={t('cleaningDetails.dayOfWeek')}
                  type="number"
                  inputProps={{ min: 0, max: 6 }}
                  value={recForm.day_of_week}
                  onChange={handleRecChange}
                  helperText={t('cleaningDetails.dayOfWeekHelp')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
            {recForm.schedule === 'every_n_weeks' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="day_of_week"
                    label={t('cleaningDetails.dayOfWeek')}
                    type="number"
                    inputProps={{ min: 0, max: 6 }}
                    value={recForm.day_of_week}
                    onChange={handleRecChange}
                    helperText={t('cleaningDetails.dayOfWeekHelp')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="every_weeks"
                    label={t('cleaningDetails.everyWeeksInterval')}
                    type="number"
                    inputProps={{ min: 1, max: 52 }}
                    value={recForm.every_weeks}
                    onChange={handleRecChange}
                    helperText={t('cleaningDetails.everyNWeeksHelp')}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
            {recForm.schedule === 'monthly' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="day_of_month"
                  label={t('cleaningDetails.dayOfMonth')}
                  type="number"
                  inputProps={{ min: 1, max: 31 }}
                  value={recForm.day_of_month}
                  onChange={handleRecChange}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="start_date"
                label={t('cleaningDetails.startDate')}
                type="date"
                value={recForm.start_date}
                onChange={handleRecChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="end_date"
                label={t('cleaningDetails.endDate')}
                type="date"
                value={recForm.end_date}
                onChange={handleRecChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="start_time"
                label={t('cleaningDetails.startTime')}
                type="time"
                value={recForm.start_time}
                onChange={handleRecChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecDialogOpen(false)} disabled={savingRec}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={saveRecurrence} disabled={savingRec}>
            {t('cleaningDetails.createSchedule')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cleanerDialogOpen}
        onClose={() => setCleanerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('cleaningDetails.cleanersDialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder={t('cleaningDetails.cleanersSearchPlaceholder')}
            value={cleanerPickerQuery}
            onChange={(e) => setCleanerPickerQuery(e.target.value)}
            sx={{ mb: 2, mt: 0.5 }}
          />
          <List dense sx={{ maxHeight: 360, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {cleanersPickList.map((e) => (
              <ListItemButton key={e.id} onClick={() => addCleanerFromPicker(e)}>
                <ListItemText primary={e.name || `#${e.id}`} />
              </ListItemButton>
            ))}
          </List>
          {cleanersPickList.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {t('cleaningDetails.cleanersPickerEmpty')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanerDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clientDialogOpen} onClose={() => setClientDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('cleaningDetails.addClientTitle')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                name="name"
                label={t('cleaningDetails.clientName')}
                value={clientForm.name}
                onChange={handleClientFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="phone"
                label={t('cleaningDetails.clientPhone')}
                value={clientForm.phone}
                onChange={handleClientFormChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                name="email"
                label={t('cleaningDetails.clientEmail')}
                value={clientForm.email}
                onChange={handleClientFormChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDialogOpen(false)} disabled={savingClient}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" onClick={saveClient} disabled={savingClient}>
            {savingClient ? t('common.saving') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default CleaningDetailForm;
